<template>
  <v-container>
    <v-container>
      <v-expansion-panels
        class="ma-2"
        accordion>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span>
              <v-badge dot bottom color="warning" v-if="isAnyBasicFilter">
                {{ $t('filter.tab_basic') }}
              </v-badge>
              <span v-else>
                {{ $t('filter.tab_basic') }}
              </span>
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-select
              v-model="artworkTypeId"
              :items="artworkTypeList"
              item-text="name"
              item-value="id"
              clearable
              :label="$t('fields.type')">
            </v-select>
            <v-text-field
              v-model="evidenceNumber"
              :label="$t('fields.evidenceNumber')"
              counter="128"
              clearable>
            </v-text-field>
            <v-text-field
              v-model="name"
              :label="$t('fields.name')"
              counter="128"
              clearable>
            </v-text-field>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span>
              <v-badge dot bottom color="warning" v-if="isAnyAdvancedFilter">
                {{ $t('filter.tab_advanced') }}
              </v-badge>
              <span v-else>
                {{ $t('filter.tab_advanced') }}
              </span>
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
             <CodelistComboBox
              v-model="selectedAuthorList"
              :items="authorList"
              :loading="isDataRefreshing"
              :label="$t('author.title_plural')"
              :prompt="$t('filter.select_authors_prompt')"
              icon="mdi-account-outline"
              color="cyan lighten-3">
            </CodelistComboBox>

            <CodelistComboBox
              v-model="selectedTechniqueList"
              :items="techniqueList"
              :loading="isDataRefreshing"
              :label="$t('technique.title_plural')"
              :prompt="$t('filter.select_techiques_prompt')"
              icon="mdi-brush-outline">
            </CodelistComboBox>

            <CodelistComboBox
              v-if="false"
              v-model="selectedStyleList"
              :items="styleList"
              :loading="isDataRefreshing"
              :label="$t('style.title_plural')"
              :prompt="$t('filter.select_styles_prompt')"
              icon="mdi-palette-swatch-outline">
            </CodelistComboBox>

            <CodelistComboBox
              v-model="selectedThemeList"
              :items="themeList"
              :loading="isDataRefreshing"
              :label="$t('theme.title_plural')"
              :prompt="$t('filter.select_themes_prompt')"
              icon="mdi-shape-outline"
              color="lime lighten-3">
            </CodelistComboBox>

            <CodelistComboBox
              v-model="selectedMaterialList"
              :items="materialList"
              :loading="isDataRefreshing"
              :label="$t('material.title_plural')"
              :prompt="$t('filter.select_materials_prompt')"
              icon="mdi-receipt-outline">
            </CodelistComboBox>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="datingYearFrom"
                  type="number"
                  clearable
                  :label="$t('filter.dating_year_from')">
                </v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="datingYearTo"
                  type="number"
                  clearable
                  :label="$t('filter.dating_year_to')">
                </v-text-field>
              </v-col>
            </v-row>
            <v-row class="mt-0">
              <v-col cols="6">
                <v-text-field v-if="isNotLink"
                  v-model="acquisitionYearFrom"
                  type="number"
                  clearable
                  :label="$t('filter.acquisition_year_from')">
                </v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field v-if="isNotLink"
                  v-model="acquisitionYearTo"
                  type="number"
                  clearable
                  :label="$t('filter.acquisition_year_to')">
                </v-text-field>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <v-btn
        color="primary"
        @click="filter"
        :loading="isFilterLoading">
        <v-icon small class="mr-2">
          mdi-magnify
        </v-icon>
        {{ $t('filter.filter_button') }}
        </v-btn>
      <v-btn
        color="deep-orange lighten-3"
        @click="resetFilter">
        <v-icon small class="mr-2">
          mdi-filter-remove-outline
        </v-icon>
        {{ $t('filter.reset_filter_button') }}
        </v-btn>

      <v-data-table class="py-5"
        :loading="isFilterLoading"
        :headers="headers"
        :items="artworkList"
        :no-data-text="$t('table_settings.no_data')"
        :loading-text="$t('table_settings.loading_data')"
        @page-count="pageCount = $event"
        hide-default-footer
        :page.sync="page"
        :items-per-page="itemsPerPage"
        :header-props="{
          sortByText: $t('table_settings.sort_by')
          }">
        <template v-slot:[`item.thumbnailFileId`]="{ item }">
          <Thumbnail :fileId="item.thumbnailFileId"></Thumbnail>
        </template>
        <template v-slot:[`item.evidenceNumber`]="{ item }">
          {{ `${item.type.code}/${item.evidenceNumber}` }}
        </template>
        <template v-slot:[`item.authors`]="{ item }">
          <v-chip x-small color="cyan lighten-3" class="ma-1" v-for="author in item.authors" :key="author.id">
            {{ author.name }}
          </v-chip>
        </template>
        <template v-slot:[`item.themes`]="{ item }">
          <v-chip x-small color="lime lighten-3" class="ma-1" v-for="theme in item.themes" :key="theme.id">
            {{ theme.name }}
          </v-chip>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            @click="openArtwork(item.id, false)"
            fab
            color="info"
            x-small>
            <v-icon class="ma-1">mdi-eye-outline</v-icon>
          </v-btn>
          <v-btn
            v-if="currentExportBox && !item.isInExportBox"
            @click="addArtworkToExportBox(item)"
            fab
            color="success"
            x-small>
            <v-icon class="ma-1">mdi-archive-plus-outline</v-icon>
          </v-btn>
          <v-btn
            v-if="currentExportBox && item.isInExportBox"
            @click="removeArtworkFromExportBox(item)"
            fab
            color="error"
            x-small>
            <v-icon class="ma-1">mdi-archive-minus-outline</v-icon>
          </v-btn>
          <v-btn v-if="isNotLink"
            @click="deleteArtwork(item)"
            fab
            color="error"
            x-small>
            <v-icon class="ma-1">mdi-delete-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>
      <div class="text-center d-flex flex-row align-center flex-wrap">
        <div class="ma-auto text-caption">{{ `${itemFrom}-${itemTo} ${$t('table_settings.of')} ${itemsTotal}` }}</div>
        <div class="ma-1 flex-1 me-auto">
          <v-pagination
            v-model="page"
            density="compact"
            :length="pageCount"
            :total-visible="7"
          ></v-pagination>
        </div>
        <div class="ma-1 text-caption">{{ $t('table_settings.items_per_page') }}</div>
        <div class="ma-1">
          <v-select
            v-model="itemsPerPage"
            :items="itemsPerPageOptions"
            style=".override-class .v-input { display: inline-block; width: 60px; }">
          </v-select>
        </div>
      </div>
      <div class="mx-10">
      <v-col class="text-right">
        <v-btn fab small color="primary" @click="addArtwork" absolute>
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-col>
    </div>
    </v-container>
    <v-snackbar v-model="showNoBoxErrorSnackbar" color="error">
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="showExportBoxList()"
        >
          {{ $t('export_box.box_list') }}
        </v-btn>
      </template>
      {{ $t('export_box.no_box_selected') }}
    </v-snackbar>
    <v-snackbar v-model="showSnackbar" :color="snackbarColor">
      {{ this.snackbarText }}
    </v-snackbar>
    <v-dialog
      v-model="showEditDialog"
      persistent
      max-width="1024px">
        <ArtWorkEdit
          @onClose="closeArtwork"
          @onNext="queryNextArtwork"
          @onPrevious="queryPreviousArtwork"
          @onSaveSuccess="saveArtworkSuccess"
          @onSaveError="saveArtworkError">
        </ArtWorkEdit>
    </v-dialog>
    <confirm-dialog :show-dialog="this.showDeleteDialog"
      confirm-color="error"
      :question-text="$t('common_actions.confirm_delete')"
      :positive-text="$t('common_actions.delete')"
      :negative-text="$t('common_actions.cancel')"
      @onCancel="cancelDeleteArtwork"
      @onConfirm="confirmDeleteArtwork">
    </confirm-dialog>
  </v-container>
</template>

<script>
import CodelistComboBox from '@/components/filter/CodelistComboBox'
import Thumbnail from '@/components/common/Thumbnail'
import i18n from '@/plugins/i18n'
import ArtWorkEdit from '@/components/ArtWorkEdit.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import NotificationHubConnection from '@/hub/NotificationHubConnection'
import BarcodeGroup from '@/hub/groups/BarcodeGroup'
import ArtworkService from '@/services/artwork/ArtworkService'

export default {
  components: { CodelistComboBox, Thumbnail, ArtWorkEdit, ConfirmDialog },
  data: () => ({
    page: 1,
    itemsPerPage: 50,
    showDeleteDialog: false,
    artworkToDelete: null,
    showEditDialog: false,
    showNoBoxErrorSnackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
    showSnackbar: false,
    successSnackbarText: '',
    isFilterLoading: false,
    isDataRefreshing: false,
    selectedAuthorList: [],
    selectedTechniqueList: [],
    selectedStyleList: [],
    selectedThemeList: [],
    selectedMaterialList: [],
    datingYearFrom: '',
    datingYearTo: '',
    acquisitionYearFrom: '',
    acquisitionYearTo: '',
    artworkTypeId: null,
    name: '',
    evidenceNumber: '',
    itemsPerPageOptions: [ 10, 25, 50, 100 ],
    headers: [
      {
        text: i18n.t('filter.artwork_photo'),
        value: 'thumbnailFileId',
        align: 'start',
        sortable: false
      },
      {
        text: i18n.t('filter.artwork_name'),
        value: 'name',
        align: 'start'
      },
      {
        text: i18n.t('filter.artwork_evidence_number'),
        value: 'evidenceNumber',
        align: 'start'
      },
      {
        text: i18n.t('filter.artwork_authors'),
        value: 'authors',
        align: 'start'
      },
      {
        text: i18n.t('filter.artwork_themes'),
        value: 'themes',
        align: 'start'
      },
      {
        text: i18n.t('filter.actions'),
        value: 'actions',
        align: 'center',
        width: 150,
        sortable: false
      }
    ]
  }),
  methods: {
    createFilter () {
      var filter = {
        collectionId: this.currentCollection.id,
        authors: this.selectedAuthorList.filter(x => x.hasOwnProperty('id')).map(x => x.id),
        themes: this.selectedThemeList.filter(x => x.hasOwnProperty('id')).map(x => x.id),
        styles: this.selectedStyleList.filter(x => x.hasOwnProperty('id')).map(x => x.id),
        techniques: this.selectedTechniqueList.filter(x => x.hasOwnProperty('id')).map(x => x.id),
        materials: this.selectedMaterialList.filter(x => x.hasOwnProperty('id')).map(x => x.id),
        datingYearFrom: this.datingYearFrom,
        datingYearTo: this.datingYearTo,
        acquisitionYearFrom: this.acquisitionYearFrom,
        acquisitionYearTo: this.acquisitionYearTo,
        artworkTypeId: this.artworkTypeId,
        name: this.name,
        evidenceNumber: this.evidenceNumber
      }

      return filter
    },
    filter () {
      this.isFilterLoading = true

      var filter = this.createFilter()
      this.$store.commit('artworks/SET_CURRENT_FILTER', filter)

      this.$store.dispatch('artworks/filterArtworkList').then(result => {
        this.isFilterLoading = false
      }, _ => {
        this.isFilterLoading = false
      })
    },
    resetFilter () {
      this.selectedMaterialList = []
      this.selectedAuthorList = []
      this.selectedStyleList = []
      this.selectedTechniqueList = []
      this.selectedThemeList = []
      this.datingYearFrom = ''
      this.datingYearTo = ''
      this.acquisitionYearFrom = ''
      this.acquisitionYearTo = ''
      this.artworkTypeId = null
      this.name = ''
      this.evidenceNumber = ''

      var filter = this.createFilter()

      this.$store.commit('artworks/SET_CURRENT_FILTER', filter)
    },
    queryPreviousArtwork () {
      // TODO: loading
      this.$store.dispatch('artworks/cleanupUploadSession').then(_ => {
        this.$store.dispatch('artworks/queryPreviousArtwork').then(_ => {
          this.$store.dispatch('artworks/getCurrentArtworkData')
        })
      })
    },
    queryNextArtwork () {
      // TODO: loading
      this.$store.dispatch('artworks/cleanupUploadSession').then(_ => {
        this.$store.dispatch('artworks/queryNextArtwork').then(result => {
          this.$store.dispatch('artworks/getCurrentArtworkData')
        })
      })
    },
    closeArtwork () {
      this.$store.dispatch('artworks/cleanupUploadSession')
      this.$store.commit('artworks/SET_IS_ANY_CHANGE', false)
      this.showEditDialog = false
    },
    openArtwork (artworkId, force) {
      // TODO: loading
      this.$store.commit('artworks/SET_CURRENT_ARTWORK_ID', artworkId)

      var refreshPromises = []

      if (this.isNotLink) {
        refreshPromises = [
          this.$store.dispatch('artworks/clearUploadSession'),
          this.$store.dispatch('techniques/refreshTechniques'),

          this.$store.dispatch('dimensions/refreshDimensions'),
          this.$store.dispatch('locations/refreshLocations'),
          this.$store.dispatch('priceTypes/refreshPriceTypes'),
          this.$store.dispatch('currencies/refreshCurrencies')
        ]
      } else {
        refreshPromises = [
          this.$store.dispatch('artworks/clearUploadSession'),
          this.$store.dispatch('techniques/refreshTechniques')
        ]
      }

      Promise.all(refreshPromises)
        .then(_ => {
          if (force) {
            this.$store.commit('artworks/SET_CURRENT_FILTER_POSITION', 0)
            this.$store.commit('artworks/SET_CURRENT_FILTER_TOTAL', 1)
          } else {
            this.$store.dispatch('artworks/queryCurrentArtwork')
          }
        })
        .then(_ => this.$store.dispatch('artworks/getCurrentArtworkData'))
        .then(_ => {
          if (this.currentArtworkId === 0) {
            if (this.artworkTypeList.length > 0) {
              this.currentArtwork.type = this.artworkTypeList[0]
            }

            this.currentArtwork.collectionId = this.currentCollection.id
          }

          this.showEditDialog = true
        })
        .catch(ex => { this.showError(i18n.t('common_error.could_not_load_items')) })
    },
    removeArtworkFromExportBox (artwork) {
      if (this.currentExportBoxId === 0) {
        this.showNoBoxErrorSnackbar = true
        return
      }

      this.showSuccessSnackbar = false
      this.$store.dispatch('exportBoxes/removeArtworkFromExportBox', { boxId: this.currentExportBoxId, artwork: artwork }).then(_ => {
        this.showSuccess(i18n.t('export_box.item_removed'))
        artwork.isInExportBox = false
      }, error => {
        if (error.response && error.response.data === 1) {
          this.showError(i18n.t('export_box.item_already_present'))
          return
        }
        this.showError(i18n.t('export_box.general_item_add_error'))
      })
    },
    addArtworkToExportBox (artwork) {
      if (this.currentExportBoxId === 0) {
        this.showNoBoxErrorSnackbar = true
        return
      }
      this.showSuccessSnackbar = false
      this.$store.dispatch('exportBoxes/addArtworkToExportBox', { boxId: this.currentExportBoxId, artwork: artwork }).then(_ => {
        this.showSuccess(i18n.t('export_box.item_added'))
        artwork.isInExportBox = true
      }, error => {
        if (error.response && error.response.data === 1) {
          this.showError(i18n.t('export_box.item_already_present'))
          return
        }
        this.showError(i18n.t('export_box.general_item_add_error'))
      })
    },
    showExportBoxList () {
      this.$router.push('/Export/BoxList')
    },
    addArtwork () {
      this.openArtwork(0, false)
    },
    showError (text) {
      this.snackbarColor = 'error'
      this.snackbarText = text
      this.showSnackbar = true
    },
    showSuccess (text) {
      this.snackbarColor = 'success'
      this.snackbarText = text
      this.showSnackbar = true
    },
    showWarning (text) {
      this.snackbarColor = 'warning'
      this.snackbarText = text
      this.showSnackbar = true
    },
    saveArtworkSuccess () {
      this.showSuccess(i18n.t('artwork.save_success'))
      this.$store.dispatch('artworks/updateFilterItems')
    },
    saveArtworkError (error) {
      if (error !== null) {
        this.showError(i18n.t('common_error_code.' + error))
      } else {
        this.showError(i18n.t('common_error.could_not_edit_item'))
      }
    },
    deleteArtwork (artwork) {
      this.artworkToDelete = artwork
      this.showDeleteDialog = true
    },
    cancelDeleteArtwork () {
      this.showDeleteDialog = false
      this.artworkToDelete = null
    },
    confirmDeleteArtwork () {
      this.$store.dispatch('artworks/deleteArtwork', this.artworkToDelete).then(_ => {
        this.showDeleteDialog = false
      }).catch(ex => {
        console.log(ex)
        this.showDeleteDialog = false
      })
    }
  },
  computed: {
    itemFrom () {
      return (this.itemsPerPage * (this.page - 1)) + 1
    },
    itemTo () {
      const from = this.itemsPerPage * (this.page - 1)
      return from + Math.min(this.itemsPerPage, this.itemsTotal - from)
    },
    itemsTotal () {
      return this.artworkList.length
    },
    pageCount () {
      const rest = this.artworkList.length % this.itemsPerPage
      return Math.floor(this.artworkList.length / this.itemsPerPage) + (rest > 0 ? 1 : 0)
    },
    currentArtworkId () {
      return this.$store.getters['artworks/currentArtworkId']
    },
    currentArtwork () {
      return this.$store.getters['artworks/currentArtwork']
    },
    currentExportBoxId () {
      return this.$store.getters['exportBoxes/currentExportBoxId']
    },
    currentExportBox () {
      return this.$store.getters['exportBoxes/currentExportBox']
    },
    artworkList () {
      return this.$store.getters['artworks/currentFilterList']
    },
    artworkTypeList () {
      return this.$store.getters['artworkTypes/artworkTypesList']
    },
    authorList () {
      return this.$store.getters['authors/authorsList']
    },
    techniqueList () {
      return this.$store.getters['techniques/techniquesList']
    },
    styleList () {
      return this.$store.getters['styles/stylesList']
    },
    themeList () {
      return this.$store.getters['themes/themesList']
    },
    materialList () {
      return this.$store.getters['materials/materialsList']
    },
    isAnyAuthorFilter () {
      return this.selectedAuthorList.length > 0
    },
    isAnyTechniqueFilter () {
      return this.selectedTechniqueList.length > 0
    },
    isAnyStyleFilter () {
      return this.selectedStyleList.length > 0
    },
    isAnyThemeFilter () {
      return this.selectedThemeList.length > 0
    },
    isAnyMaterialFilter () {
      return this.selectedMaterialList.length > 0
    },
    isAnyBasicFilter () {
      return this.artworkTypeId != null || (this.name !== '' && this.name != null) || (this.evidenceNumber !== '' && this.evidenceNumber != null)
    },
    isAnyAdvancedFilter () {
      return (this.datingYearFrom !== '' && this.datingYearFrom != null) ||
        (this.datingYearTo !== '' && this.datingYearTo != null) ||
        (this.acquisitionYearFrom !== '' && this.acquisitionYearFrom != null) ||
        (this.acquisitionYearTo !== '' && this.acquisitionYearTo != null) ||
        this.isAnyAuthorFilter || this.isAnyTechniqueFilter || this.isAnyStyleFilter || this.isAnyThemeFilter || this.isAnyMaterialFilter
    },
    isAnyChange () {
      return this.$store.getters['artworks/isAnyChange']
    },
    currentCollection () {
      return this.$store.getters['collectionContext/currentCollection']
    },
    isNotLink () {
      return this.$store.getters['authentication/accessType'] > 1
    },
    isBarcodeReaderEnabled () {
      return this.$store.getters['localSettings/isLabelPrinterEnabled']
    }
  },
  // BIG TODO: MISSING LOADING ERROR NOTIFICATIONS
  mounted () {
    let refreshPromises = [
      this.$store.dispatch('artworkTypes/refreshArtworkTypes'),
      this.$store.dispatch('authors/refreshAuthors'),
      this.$store.dispatch('styles/refreshStyles'),
      this.$store.dispatch('themes/refreshThemes'),
      this.$store.dispatch('techniques/refreshTechniques'),
      this.$store.dispatch('materials/refreshMaterials')
    ]

    if (this.currentExportBox) {
      refreshPromises.push(this.$store.dispatch('exportBoxes/refreshExportBoxContent', this.currentExportBox))
    }

    this.isDataRefreshing = true

    Promise.all(refreshPromises).then(
      _ => {
        this.isDataRefreshing = false
      }).catch(ex => { this.isDataRefreshing = false; console.log(ex) })

    if (this.isBarcodeReaderEnabled) {
      NotificationHubConnection.connect()
      BarcodeGroup.joinBarcodeGroup((data) => {
        if (this.isAnyChange) {
          this.showWarning(i18n.t('artwork.barcode_invalid_state'))
          return
        }

        ArtworkService.getArtworkIdByIdentification(data.data).then((result) => {
          if (result.artworkId === 0) {
            this.showWarning(i18n.t('artwork.barcode_not_found'))
          } else {
            this.showSuccess(i18n.t('artwork.barcode_found'))

            if (this.showEditDialog) {
              this.$store.dispatch('artworks/queryArtwork', result.artworkId)
            } else {
              this.openArtwork(result.artworkId, true)
            }
          }
        }, _ => {
          this.showError(i18n.t('artwork.barcode_error'))
        })
      })
    }
  },
  beforeDestroy () {
    if (this.isBarcodeReaderEnabled) {
      BarcodeGroup.leaveBarcodeGroup()
      // NotificationHubConnection.disconnect()
    }
  }
}

</script>



// WEBPACK FOOTER //
// src/components/Filter.vue